import { add, endOfDay, Interval, startOfDay } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Box, Button, Divider, Grid } from '@mui/material'

import { Game } from '../..'
import { DateRangeChangeReason, DateRangeValue } from '../../../../components/DateRangePicker/DateRangePicker'
import GRCircularProgress from '../../../../components/GRCircularProgress/GRCircularProgress'
import { GamePerformaceDialogData, GamePerformanceDialog } from '../../../../components/GamePerformanceDialog/GamePerformanceDialog'
import { LockedFeature } from '../../../../components/LockedFeature/LockedFeature'
import { PerformanceChartV2DataType } from '../../../../components/PerformanceChartV2/PerformanceChartV2DataType'
import { getIntervalByGranularity } from '../../../../helpers/date'
import { dayMilliseconds } from '../../../../helpers/dayMilliseconds'
import { useAppDispatch, useAppSelector } from '../../../../hooks/storeHooks'
import { useDebouncedResize } from '../../../../hooks/useDebouncedResize'
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue'
import {
  maxDayDifferenceForDateRange,
  maxLiveEventsCalendarZoomInMilliseconds,
  minLiveEventsCalendarZoomInMilliseconds,
  resolveYAxisDataTypes,
} from '../../../../pages/LiveEventsTrackerPage/LiveEventsTrackerPage'
import analyticsService from '../../../../services/AnalyticsService'
import languageService from '../../../../services/LanguageService'
import utilsService from '../../../../services/UtilsService'
import { LockedFeatureId } from '../../../../types/LockedFeature'
import { useInternalAccessCheck, useLiveEventsTrackerAccessCheck } from '../../../account/hooks/roleHooks'
import { Analysis } from '../../../analysis/types/Analysis'
import { LiveEventsCalendarByGameContainer } from '../../../live-events/components/LiveEventsCalendar/LiveEventsCalendarByGameContainer/LiveEventsCalendarByGameContainer'
import { LiveEventsFilters, LiveEventsFiltersConfig } from '../../../live-events/components/LiveEventsFilters/LiveEventsFilters'
import { PerformanceDialogEventsContainer } from '../../../live-events/components/PerformanceDialogEventsContainer/PerformanceDialogEventsContainer'
import {
  defaultTrackedEventsStartTimestamp,
  lockedLiveEventsTrackerEndTimestamp,
  lockedLiveEventsTrackerStartTimestamp,
} from '../../../live-events/const/const'
import { EventDialogTab } from '../../../live-events/hooks/useEventDialogTabs'
import { useLiveEventModal } from '../../../live-events/hooks/useLiveEventModal'
import { useLiveEventsAnalystNotesModal } from '../../../live-events/hooks/useLiveEventsAnalystNotesModal'
import { useLiveEventsAnalystOverviewModal } from '../../../live-events/hooks/useLiveEventsAnalystOverviewModal'
import { useLiveEventsByCalendarTimeRanges } from '../../../live-events/hooks/useLiveEventsByCalendarTimeRanges'
import {
  useLiveEventsCalendarArchetypeClickHandler,
  useLiveEventsCalendarGroupClickHandler,
} from '../../../live-events/hooks/useLiveEventsCalendarGroupClickHandler'
import { useLiveEventsCommonFilters } from '../../../live-events/hooks/useLiveEventsCommonFilters'
import { useLiveEventsPickerMaxDate } from '../../../live-events/hooks/useLiveEventsPickerMaxDate'
import { onlyMixpanelTrackingServicesToExclude } from '../../../live-events/hooks/useLiveEventsTrackerAnalyticsEvents'
import { useLiveEventsTrackingSearchParams } from '../../../live-events/hooks/useLiveEventsTrackingSearchParams'
import { useTrackedGame } from '../../../live-events/hooks/useTrackedGame'
import { useTrackedGamesCurrencies } from '../../../live-events/hooks/useTrackedGamesCurrencies'
import { useFilteredTrackedGamesEvents } from '../../../live-events/hooks/useTrackedGamesEvents'
import { selectHighlightedEvent } from '../../../live-events/slices/liveEventModalSlice'
import { visibleGameCalendarsChanged } from '../../../live-events/slices/liveEventsCalendarsSlice'
import { CalendarTimeRanges } from '../../../live-events/types/Calendar'
import { EventsDataFetchingMode } from '../../../live-events/types/EventsDataFetchingMode'
import {
  AnalystReviewTimelineItem,
  LiveEventsCalendarTimelineType,
  PerformanceEffectTimelineItem,
  TrackingEventTimelineItem,
} from '../../../live-events/types/LiveEvents'
import { PerformanceChangesByOptionValue } from '../../../live-events/types/PerformanceChangesByOption'
import { PerformanceEffectType } from '../../../live-events/types/PerformanceEffect'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { TrackingEventTimeRange } from '../../../live-events/types/TrackingEvents'
import { applyDateRangeToCalendarTimeRanges } from '../../../live-events/utils/applyDateRangeToCalendarTimeRanges'
import { useCurrentMarket } from '../../../markets'
import { GranularityValue } from '../../../revenue-and-downloads/types/Filters'
import './GameLiveEvents.scss'

const emptyArray = [] as any[]

interface Props {
  game: Game
  analysis: Analysis | undefined
  onLiveEventsLoadingChange?: (loading: boolean) => void
}

const GameLiveEvents: React.FC<Props> = ({ game, analysis, onLiveEventsLoadingChange }) => {
  useEffect(() => {
    analyticsService.trackEvent('Visited Game Overview: Live Events', {
      data: {
        gameId: game.id,
        gameName: game.resolvedName,
        analysisId: analysis?.id,
      },
    })
  }, [analysis?.id, game.id, game.resolvedName])

  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const isInternalUser = useInternalAccessCheck()
  const { currentMarketIso } = useCurrentMarket()
  const { parsedParams, setSearchParams } = useLiveEventsTrackingSearchParams()
  const [eventNameSearchValue] = useState<string>('')
  const [calendarTimeRanges, setCalendarTimeRanges] = useState<CalendarTimeRanges>({})
  const debouncedCalendarTimeRanges = useDebouncedValue(calendarTimeRanges, 300)
  const highlightedEventId = useAppSelector(selectHighlightedEvent)
  const [gamePerformanceDialogData, setGamePerformanceDialogData] = useState<GamePerformaceDialogData>()
  const [selectedPerformanceChartInterval, setSelectedPerformanceChartInterval] = useState<Interval>()
  const handleChartClick = setSelectedPerformanceChartInterval
  const debouncedResize = useDebouncedResize(50)
  const handleCalendarRerender = useCallback(debouncedResize, [debouncedResize])
  const handleCalendarEventGroupClick = useLiveEventsCalendarGroupClickHandler()

  const { showModal: showEventModal } = useLiveEventModal()
  const { showModal: showAnalystNotesModal } = useLiveEventsAnalystNotesModal()
  const { showModal: showAnalystOverviewModal } = useLiveEventsAnalystOverviewModal()

  const { data: trackedGame, isFetching: isLoadingTrackedGame } = useTrackedGame({ gameId: game.id, skip: !game.isTracked })

  const trackedGameArray = useMemo(() => {
    if (trackedGame && trackedGame.allowed) {
      return [trackedGame]
    }

    return []
  }, [trackedGame])

  const gameIdArray = useMemo(() => {
    if (trackedGame && trackedGame.allowed) {
      return [trackedGame.game.id]
    }

    return []
  }, [trackedGame])

  const selectedGameCurrencies = useTrackedGamesCurrencies({ gameIds: gameIdArray, allTrackedGames: trackedGameArray })

  const trackedEventsResult = useLiveEventsByCalendarTimeRanges({
    calendarTimeRanges: debouncedCalendarTimeRanges,
    trackedGameIds: gameIdArray,
    fallbackDateFrom: parsedParams.dateFrom,
    fallbackDateTo: parsedParams.dateTo,
    dataFetchingMode: EventsDataFetchingMode.PerGame,
  })

  const trackedEvents = useMemo(
    () => ({
      ...trackedEventsResult,
      stats: trackedEventsResult.stats ?? {},
    }),
    [trackedEventsResult]
  )

  useEffect(() => {
    const loading = !!(trackedEvents.isLoading || trackedEvents.isFetching)
    onLiveEventsLoadingChange?.(loading)
    return () => {
      onLiveEventsLoadingChange?.(false)
    }
  }, [trackedEvents.isLoading, trackedEvents.isFetching, onLiveEventsLoadingChange])

  const handleCalendarArchetypeClick = useLiveEventsCalendarArchetypeClickHandler(trackedEvents.events)

  const { commonFilters } = useLiveEventsCommonFilters({
    selectedGameIds: gameIdArray,
    trackedEvents,
    parsedParams: {
      liveEventTags: parsedParams.liveEventTags,
      liveEventSecondaryTags: parsedParams.liveEventSecondaryTags,
      motivations: parsedParams.motivations,
      archetypes: parsedParams.archetypes,
      liveEventDurations: parsedParams.liveEventDurations,
      liveEventAppearances: parsedParams.liveEventAppearances,
      liveEventPricePoints: parsedParams.liveEventPricePoints,
      additionalDatas: parsedParams.additionalDatas,
    },
  })

  const filters = useMemo(
    () => ({
      selectedLiveEventTags: commonFilters.liveEventTags,
      selectedLiveEventSecondaryTags: commonFilters.liveEventSecondaryTags,
      selectedLiveEventsDurationIdsMap: commonFilters.liveEventDurations,
      selectedLiveEventsAppearanceIdsMap: commonFilters.liveEventAppearances,
      selectedLiveEventsPricePointIdsMap: commonFilters.liveEventPricePoints,
      selectedMotivationsIdsMap: commonFilters.motivations,
      selectedArchetypesIdsMap: commonFilters.archetypes,
      performanceEffectThreshold: parsedParams.performanceEffectThreshold,
      performanceChangesBy: parsedParams.performanceChangesBy,
      eventNameSearchValue,
    }),
    [
      commonFilters.liveEventTags,
      commonFilters.liveEventSecondaryTags,
      commonFilters.liveEventDurations,
      commonFilters.liveEventAppearances,
      commonFilters.liveEventPricePoints,
      commonFilters.motivations,
      commonFilters.archetypes,
      parsedParams.performanceEffectThreshold,
      parsedParams.performanceChangesBy,
      eventNameSearchValue,
    ]
  )

  const filteredTrackingEventsByGame = useFilteredTrackedGamesEvents({
    currencies: selectedGameCurrencies,
    data: trackedEvents,
    ...filters,
  })

  const loadedEventsTimeRange: TrackingEventTimeRange | undefined = useMemo(() => {
    if (trackedEvents.isFetching) return undefined
    const events = trackedEvents.events[game.id]
    if (!events || events.length === 0) return undefined
    return events.reduce(
      (acc, e) => ({
        minTimeData:
          startOfDay(new Date(e.start)).getTime() < acc.minTimeData || acc.minTimeData === 0 ? startOfDay(new Date(e.start)).getTime() : acc.minTimeData,
        maxTimeData: endOfDay(new Date(e.end)).getTime() > acc.maxTimeData ? endOfDay(new Date(e.end)).getTime() : acc.maxTimeData,
      }),
      { minTimeData: 0, maxTimeData: 0 }
    )
  }, [trackedEvents.events, trackedEvents.isFetching, game.id])

  const maxTrackingEndedForPicker = useMemo(
    () => Object.values(trackedEvents.stats ?? {}).reduce((max, stat) => (stat.trackingEnded > max ? stat.trackingEnded : max), 0),
    [trackedEvents.stats]
  )

  // Only events the current user can see count as meaningful data for the picker max
  // (non-internal users cannot see future-start events).
  const hasVisibleEvents = useMemo(() => {
    const futureEventCutoff = isInternalUser ? undefined : endOfDay(new Date()).getTime()
    return Object.values(trackedEvents.events).some((events) => events.some((e) => futureEventCutoff === undefined || e.start <= futureEventCutoff))
  }, [trackedEvents.events, isInternalUser])

  // High-water mark per market + game: moving the date range far into the past narrows the
  // fetched event window, which must not shrink the picker max (GR-2964). Flooring to today keeps
  // the max at the end of the current day even when only past events are loaded, so history loads
  // cannot clamp the selected range (GR-2985).
  const maxDateForPicker = useLiveEventsPickerMaxDate({
    isInternalUser,
    maxTimeFromEvents: filteredTrackingEventsByGame.maxTime,
    maxTrackingEnded: maxTrackingEndedForPicker,
    floorToToday: true,
    hasVisibleEvents,
    isFetching: trackedEvents.isLoading || trackedEvents.isFetching,
    resetKey: `${currentMarketIso}|${game.id}`,
  })

  // resolve configuration for filters based on selected tab
  const filterConfig: LiveEventsFiltersConfig = useMemo(() => {
    const config: LiveEventsFiltersConfig = {
      performanceChangesByDisabled: false,
      thresholdDisabled: false,
      sortGamesByDisabled: false,
    }

    return config
  }, [])

  const dateRange = useMemo(
    () => ({
      fromDate: parsedParams.dateFrom ? new Date(parsedParams.dateFrom) : undefined,
      toDate: parsedParams.dateTo ? new Date(parsedParams.dateTo) : undefined,
    }),
    [parsedParams.dateFrom, parsedParams.dateTo]
  )

  const gamePerformanceDialogSelectedUpdates = useMemo(() => {
    return selectedPerformanceChartInterval?.start ? [{ timestamp: selectedPerformanceChartInterval?.start }] : []
  }, [selectedPerformanceChartInterval?.start])

  const handleCalendarTimeRangeChange = useCallback(
    (calendarId: string | number, timeStart: number, timeEnd: number) => {
      // update time range only if it's within allowed limits
      if (timeEnd - timeStart <= maxLiveEventsCalendarZoomInMilliseconds + dayMilliseconds) {
        setCalendarTimeRanges((current) => {
          const newRanges = { ...current }
          if (newRanges[calendarId]) {
            newRanges[calendarId] = { timeStart, timeEnd }
          } else {
            newRanges[calendarId] = { timeStart: parsedParams.dateFrom, timeEnd: parsedParams.dateTo }
          }

          return newRanges
        })
      }
    },
    [parsedParams.dateFrom, parsedParams.dateTo]
  )

  const handleGamePerformanceDialogOpen = useCallback(
    (trackedGame: TrackedGame) => {
      setGamePerformanceDialogData({
        appId: trackedGame.game.appId,
        gameName: trackedGame.game.resolvedName,
        marketIso: currentMarketIso,
        initialYAxisLeftConfig: { dataType: PerformanceChartV2DataType.Revenue },
        initialYAxisRightConfig: { dataType: PerformanceChartV2DataType.Downloads },
        initialGranularity: GranularityValue.Week,
      })
    },
    [currentMarketIso]
  )

  const handlePerformanceDialogClose = useCallback(() => {
    setGamePerformanceDialogData(undefined)
    setSelectedPerformanceChartInterval(undefined)
  }, [])

  const getPerformanceDialogInstanceTableInterval = useCallback(
    (granularity: GranularityValue) => {
      return selectedPerformanceChartInterval
        ? selectedPerformanceChartInterval
        : gamePerformanceDialogData?.highlightedTimestamp
        ? getIntervalByGranularity(granularity, gamePerformanceDialogData?.highlightedTimestamp)
        : undefined
    },
    [gamePerformanceDialogData?.highlightedTimestamp, selectedPerformanceChartInterval]
  )

  const handleCalendarEventItemClick = useCallback(
    (clickedItem: TrackingEventTimelineItem | PerformanceEffectTimelineItem | AnalystReviewTimelineItem) => {
      switch (clickedItem.type) {
        case LiveEventsCalendarTimelineType.TrackingEvent:
        case LiveEventsCalendarTimelineType.TrackingEventWithGame:
          const item: TrackingEventTimelineItem = clickedItem as TrackingEventTimelineItem

          showEventModal({
            trackedGameId: item.trackedGame.game.id,
            eventTypeId: item.trackingEvent.typeId,
            eventId: item.trackingEventId,
            durationStart: item.trackingEvent.start,
            durationEnd: item.trackingEvent.end,
            tab: EventDialogTab.Description,
          })
          break

        case LiveEventsCalendarTimelineType.PerformanceEffect:
          const data = {
            gameName:
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.names['us'] ||
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.resolvedName,
            gameAppId: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.appId,
            gameConventionalSubgenre: languageService.getTranslation(
              'conventionalSubgenres',
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.conventionalSubgenreId
            ),
            source: `${clickedItem.group} Change Click`,
          }

          analyticsService.trackEvent('Live Events Tracker: Opened Game Statistics Graph', {
            data,
            serviceToExclude: onlyMixpanelTrackingServicesToExclude,
          })

          const yAxisDataTypes = resolveYAxisDataTypes((clickedItem as PerformanceEffectTimelineItem).group)
          setSelectedPerformanceChartInterval({ start: clickedItem.start_time, end: clickedItem.end_time })
          setGamePerformanceDialogData({
            appId: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.appId,
            gameName: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.resolvedName,
            marketIso: currentMarketIso,
            highlightedTimestamp: clickedItem.start_time,
            initialYAxisLeftConfig: { dataType: yAxisDataTypes[0] },
            initialYAxisRightConfig: { dataType: yAxisDataTypes[1] },
            initialGranularity:
              (clickedItem as PerformanceEffectTimelineItem).group === PerformanceEffectType.MAU ? GranularityValue.Month : GranularityValue.Day,
          })
          break
        case LiveEventsCalendarTimelineType.AnalystReview:
          const analystReviewItem: AnalystReviewTimelineItem = clickedItem as AnalystReviewTimelineItem
          showAnalystNotesModal({
            trackedGameId: analystReviewItem.review.gameId,
            commentId: analystReviewItem.review.id,
          })
      }
    },
    [currentMarketIso, showAnalystNotesModal, showEventModal]
  )

  const handleOverviewTrackedGameChanged = useCallback(
    (trackedGame: TrackedGame) => {
      showAnalystOverviewModal({
        trackedGameId: trackedGame.game.id,
      })
    },
    [showAnalystOverviewModal]
  )

  // make sure selected date starts from start of day and ends to end of day
  const handleDateRangeChange = useCallback(
    (value?: DateRangeValue, reason?: DateRangeChangeReason) => {
      let fromDate = value?.fromDate
      let toDate = value?.toDate

      // Handle fromDate and toDate setting to have maxDayDifferenceForDateRange.
      // We clamp the inclusive day count to maxDayDifferenceForDateRange so that the
      // timespan picker, calendar viewport (capped at maxLiveEventsCalendarZoomInMilliseconds)
      // and Event Stats graph all visualize the same number of days.
      //
      // See LiveEventsTrackerPage for an extended note on why this comparison is `>=`
      // instead of `>` — it covers the GR-2491 edge case where shifting a boundary on
      // an already 60-day range would otherwise extend the span to 61 days.
      if (value && value.fromDate && value.toDate) {
        const dayDifference = utilsService.getDayDifferenceFromTwoDates(value.fromDate.getTime(), value.toDate.getTime())
        if (dayDifference >= maxDayDifferenceForDateRange) {
          if (new Date(parsedParams.dateFrom as number).getTime() === fromDate?.getTime() && toDate) {
            // toDate changed: keep toDate at end-of-day, clamp fromDate so the inclusive
            // span equals maxDayDifferenceForDateRange days.
            fromDate = new Date(toDate.getTime())
            fromDate.setHours(0, 0, 0, 0)
            fromDate.setDate(fromDate.getDate() - (maxDayDifferenceForDateRange - 1))
          } else if (new Date(parsedParams.dateTo as number)?.getTime() === toDate?.getTime() && fromDate) {
            // fromDate changed: keep fromDate at start-of-day, clamp toDate so the inclusive
            // span equals maxDayDifferenceForDateRange days. toDate must be end-of-day to
            // match the picker convention applied elsewhere.
            toDate = new Date(fromDate.getTime())
            toDate.setHours(23, 59, 59, 999)
            toDate.setDate(fromDate.getDate() + (maxDayDifferenceForDateRange - 1))
          }
        }

        if (fromDate && toDate) {
          const fixedDayDifference = utilsService.getDayDifferenceFromTwoDates(fromDate.getTime(), toDate.getTime())
          const data = {
            dayDifference: fixedDayDifference,
          }

          analyticsService.trackEvent('Live Events Tracker: Date Range Day Difference Changed', {
            data,
            serviceToExclude: onlyMixpanelTrackingServicesToExclude,
          })
        }
      }

      if (parsedParams.dateFrom !== fromDate?.getTime() || parsedParams.dateTo !== toDate?.getTime()) {
        setSearchParams((currentParams) => ({ ...currentParams, dateFrom: fromDate?.getTime(), dateTo: toDate?.getTime() }))
      }

      // User picker changes always move the calendar. Automatic bounds clamps (history load
      // dropping picker max to the last tracked event) only move calendars still on the previous
      // picker range, so scrolling to a later empty month is not yanked back (GR-2985).
      setCalendarTimeRanges((currentRanges) =>
        applyDateRangeToCalendarTimeRanges(
          currentRanges,
          fromDate?.getTime(),
          toDate?.getTime(),
          reason === 'bounds' ? { onlyIfInSyncWith: { dateFrom: parsedParams.dateFrom, dateTo: parsedParams.dateTo } } : undefined
        )
      )
    },
    [parsedParams.dateFrom, parsedParams.dateTo, setSearchParams]
  )

  // make sure selected date starts from start of day and ends to end of day for feed view
  const handlePerformanceEffectThresholdChange = useCallback(
    (value: number) => {
      setSearchParams((currentParams) => ({ ...currentParams, performanceEffectThreshold: value }))
    },
    [setSearchParams]
  )

  const handlePerformanceChangesByChange = useCallback(
    (value: PerformanceChangesByOptionValue) => {
      setSearchParams((currentParams) => ({ ...currentParams, performanceChangesBy: value }))
    },
    [setSearchParams]
  )

  useEffect(() => {
    dispatch(visibleGameCalendarsChanged([game.id]))
  }, [dispatch, game])

  // initially set the default date range
  useEffect(() => {
    // TODO: checking hasAccessToLiveEventsTracker is unnecesary since we only show Feature Locked Card when user does not have the needed role
    const startDate = parsedParams.dateFrom ? new Date(parsedParams.dateFrom) : startOfDay(add(new Date(), { days: -14 }))
    const initialStartDate = hasAccessToLiveEventsTracker ? startDate : new Date(lockedLiveEventsTrackerStartTimestamp)
    const endDate = parsedParams.dateTo ? new Date(parsedParams.dateTo) : endOfDay(new Date())
    const initialEndDate = hasAccessToLiveEventsTracker ? endDate : new Date(lockedLiveEventsTrackerEndTimestamp)
    setSearchParams((currentParams) => ({ ...currentParams, dateFrom: initialStartDate.getTime(), dateTo: initialEndDate.getTime() }))
    setCalendarTimeRanges((currentRanges) => {
      const newRanges = { ...currentRanges }
      newRanges[game.id] = { timeStart: initialStartDate?.getTime(), timeEnd: initialEndDate?.getTime() }

      return newRanges
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGroupByArchetypeChanged = useCallback(() => {
    setSearchParams((currentParams) => ({ ...currentParams, groupByArchetype: !currentParams.groupByArchetype }))
  }, [setSearchParams])

  const handleHideDiscontinuedArchetypesChanged = useCallback(() => {
    setSearchParams((currentParams) => ({ ...currentParams, hideDiscontinuedArchetypes: !currentParams.hideDiscontinuedArchetypes }))
  }, [setSearchParams])

  return (
    <div className="GameLiveEvents">
      {!trackedGame ? (
        <GRCircularProgress />
      ) : (
        <>
          {hasAccessToLiveEventsTracker && trackedGame.allowed === true ? (
            <>
              {
                <Box>
                  <Grid container alignItems={'center'}>
                    <Grid item xs>
                      {t('overview:live_events_help_text')}
                    </Grid>
                    <Grid item>
                      <Link to={`/live-events-tracker/games?gameIds=${game.id}`}>
                        <Button variant="contained" color="primary" size="small">
                          {t('overview:live_events_tracker_button')}
                        </Button>
                      </Link>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 2, mb: 3 }} />
                  <Box mb={3}>
                    <LiveEventsFilters
                      dateRange={dateRange}
                      minDate={defaultTrackedEventsStartTimestamp}
                      maxDate={maxDateForPicker}
                      sortGamesBy={parsedParams.sortGamesBy}
                      threshold={parsedParams.performanceEffectThreshold}
                      performanceChangesBy={parsedParams.performanceChangesBy}
                      groupByArchetype={parsedParams.groupByArchetype}
                      hideDiscontinuedArchetypes={parsedParams.hideDiscontinuedArchetypes}
                      onDateRangeChange={handleDateRangeChange}
                      onThresholdChange={handlePerformanceEffectThresholdChange}
                      onPerformanceChangesByChange={handlePerformanceChangesByChange}
                      onGroupByArchetypeChanged={handleGroupByArchetypeChanged}
                      onHideDiscontinuedArchetypesChanged={handleHideDiscontinuedArchetypesChanged}
                      config={filterConfig}
                    />
                  </Box>

                  {calendarTimeRanges[trackedGame.game.id] && (
                    <LiveEventsCalendarByGameContainer
                      trackedGames={trackedGameArray}
                      trackedGame={trackedGame}
                      trackedEvents={filteredTrackingEventsByGame.events[trackedGame.game.id] || emptyArray}
                      gameStats={trackedEvents.stats?.[trackedGame.game.id]}
                      loadedEventsTimeRange={loadedEventsTimeRange}
                      performanceEffects={filteredTrackingEventsByGame.performanceEffects[trackedGame.game.id] || emptyArray}
                      gameTopIAPs={filteredTrackingEventsByGame.gameTopIAPs[trackedGame.game.id] || emptyArray}
                      gameVersions={filteredTrackingEventsByGame.gameVersions[trackedGame.game.id] || emptyArray}
                      analystReviews={filteredTrackingEventsByGame.comments?.[trackedGame.game.id] || emptyArray}
                      calendarTimeRanges={calendarTimeRanges}
                      onItemClick={handleCalendarEventItemClick}
                      onGroupClick={handleCalendarEventGroupClick}
                      onArchetypeClick={handleCalendarArchetypeClick}
                      onOverviewTrackedGameChanged={handleOverviewTrackedGameChanged}
                      timeStart={calendarTimeRanges[game.id] ? calendarTimeRanges[game.id].timeStart : 0}
                      timeEnd={calendarTimeRanges[game.id] ? calendarTimeRanges[game.id].timeEnd : 0}
                      minZoom={minLiveEventsCalendarZoomInMilliseconds}
                      maxZoom={maxLiveEventsCalendarZoomInMilliseconds}
                      highlightedEventId={highlightedEventId}
                      onCalendarTimeRangeChange={handleCalendarTimeRangeChange}
                      selectedCalendarAdditionalDataIdsMap={commonFilters.additionalDatas}
                      onCalendarChange={handleCalendarRerender}
                      onGamePerformanceDialogOpen={handleGamePerformanceDialogOpen}
                      performanceValues={filteredTrackingEventsByGame.performanceValues[trackedGame.game.id]}
                      performanceChanges={filteredTrackingEventsByGame.performanceChanges?.[trackedGame.game.id]}
                      groupByArchetype={parsedParams.groupByArchetype}
                      hideDiscontinuedArchetypes={parsedParams.hideDiscontinuedArchetypes}
                    />
                  )}
                </Box>
              }

              {gamePerformanceDialogData && (
                <GamePerformanceDialog
                  open={!!gamePerformanceDialogData}
                  onClose={handlePerformanceDialogClose}
                  appId={gamePerformanceDialogData!.appId}
                  marketIso={gamePerformanceDialogData!.marketIso}
                  gameName={gamePerformanceDialogData!.gameName}
                  selectedVerticalMarks={gamePerformanceDialogSelectedUpdates}
                  initialYAxisLeftConfig={gamePerformanceDialogData.initialYAxisLeftConfig}
                  initialYAxisRightConfig={gamePerformanceDialogData.initialYAxisRightConfig}
                  initialGranularity={gamePerformanceDialogData.initialGranularity}
                  onChartClick={handleChartClick}
                  selectedGame={trackedGame}
                  showNewEventsStrip
                  render={(granularity: GranularityValue, dateRange) => {
                    return (
                      <PerformanceDialogEventsContainer
                        interval={getPerformanceDialogInstanceTableInterval(granularity)}
                        dateRange={dateRange}
                        parentEvents={trackedEvents.events}
                        trackedGames={trackedGameArray}
                        appId={gamePerformanceDialogData.appId}
                        isLoading={isLoadingTrackedGame || trackedEvents.isLoading}
                      />
                    )
                  }}
                ></GamePerformanceDialog>
              )}
            </>
          ) : (
            <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventsTracker} />
          )}
        </>
      )}
    </div>
  )
}

export default GameLiveEvents
